import AssetInputPage from '../pages/AssetInputPage.js';
import FamilyInputPage from '../pages/FamilyInputPage.js';
import ResultPage from '../pages/ResultPage.js';
import InquiryPage from '../pages/InquiryPage.js';

/**
 * 相続診断アプリケーションのメインクラス
 */
export default class SouzokuDiagnosisApp {
  #currentPage = null;
  #diagnosisData = {
    currentStep: 0,
    assets: {},
    family: {
      spouse: null,
      childrenCount: 0,
      children: [],
      father: null,
      mother: null,
      siblingsCount: 0
    },
    // 診断結果（ResultPageで保存、InquiryPageで使用）
    diagnosisResult: null
  };
  #pages = {};
  #assetCategories;
  #isStarted = false;

  constructor(assetCategories) {
    this.#assetCategories = assetCategories;
    this.#initializePages();
    this.#initializeBeforeUnloadHandler();
  }

  /**
   * ページインスタンスを初期化
   */
  #initializePages() {
    this.#pages = {
      assetInput: new AssetInputPage(this),
      familyInput: new FamilyInputPage(this),
      result: new ResultPage(this),
      inquiry: new InquiryPage(this)
    };
  }

  /**
   * beforeunloadイベントハンドラーを初期化
   * 診断中にブラウザバックや閉じる操作をした場合、確認ダイアログを表示
   * ただし、tel:やmailto:などの外部プロトコルリンクは除外
   */
  #initializeBeforeUnloadHandler() {
    let allowUnload = false;

    // tel:やmailto:などの外部プロトコルリンクをクリックした時は離脱を許可
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.href) {
        const protocol = link.protocol;
        if (protocol === 'tel:' || protocol === 'mailto:') {
          allowUnload = true;
          // 100ms後に元に戻す（beforeunloadが発火する前に許可フラグを立てる）
          setTimeout(() => {
            allowUnload = false;
          }, 100);
        }
      }
    });

    window.addEventListener('beforeunload', (e) => {
      if (this.#isStarted && !allowUnload) {
        e.preventDefault();
        e.returnValue = '入力中のデータが失われますが、ページを離れてもよろしいですか？';
        return e.returnValue;
      }
    });
  }

  /**
   * データアクセサ（読み取り専用）
   */
  get data() {
    return this.#diagnosisData;
  }

  /**
   * 資産カテゴリアクセサ
   */
  get assetCategories() {
    return this.#assetCategories;
  }

  /**
   * 現在のステップアクセサ
   */
  get currentStep() {
    return this.#diagnosisData.currentStep;
  }

  /**
   * ページにナビゲート
   */
  navigateTo(pageName) {
    if (!this.#pages[pageName]) {
      console.error(`Page "${pageName}" not found`);
      return;
    }

    this.#currentPage = this.#pages[pageName];
    this.#currentPage.render();

    // スクロール位置を一番上にリセット
    window.scrollTo(0, 0);
  }

  /**
   * ステップを進める
   */
  nextStep() {
    this.#diagnosisData.currentStep++;
    this.#renderCurrentStep();
  }

  /**
   * ステップを戻す
   */
  prevStep() {
    this.#diagnosisData.currentStep--;
    this.#renderCurrentStep();
  }

  /**
   * 現在のステップをレンダリング
   */
  #renderCurrentStep() {
    const step = this.#diagnosisData.currentStep;

    if (step === 0) {
      this.navigateTo('assetInput');
    } else if (step === 1) {
      this.navigateTo('familyInput');
    } else if (step === 2) {
      this.navigateTo('result');
    } else if (step === 3) {
      this.navigateTo('inquiry');
    }
  }

  /**
   * 資産データを更新
   */
  updateAssets(assets) {
    this.#diagnosisData.assets = assets;
  }

  /**
   * 家族データを更新
   */
  updateFamily(family) {
    this.#diagnosisData.family = { ...this.#diagnosisData.family, ...family };
  }

  /**
   * 診断を開始
   */
  start() {
    // ヒーローセクションを非表示
    document.querySelector('[data-ref="heroSection"]')?.classList.add('d-none');

    // 診断コンテンツを表示
    document.querySelector('[data-ref="diagnosisContent"]')?.classList.remove('d-none');

    // 背景色をトップページから診断ページに切り替え
    document.body.classList.remove('top-page');
    document.body.classList.add('diagnosis-page');

    // 診断開始フラグをオン（beforeunloadハンドラーを有効化）
    this.#isStarted = true;

    // 最初のステップを表示
    this.#diagnosisData.currentStep = 0;
    this.#renderCurrentStep();
  }
}
